<script setup lang="ts">
import { ref, computed } from 'vue';
import { SharingOptions } from '@ilteducation/components';
import { Video } from '@ilteducation/videos';
import { shareByEmail } from '@/foundations/helpers/share-by-email';
import { DialogCloseButton } from '@ilteducation/components';
import type { ShareableContentCollection } from '@ilteducation/content-collections-types';

const emit = defineEmits<{
  (e: 'close'): void;
}>();

const props = defineProps<{
  itemToShare: Video | ShareableContentCollection;
  title: string;
  subject?: string;
}>();

const showDialog = ref(true);
const currentUrl = computed(() => window.location.href);

const displayName = computed(() => ('title' in props.itemToShare ? props.itemToShare.title : props.itemToShare.name));

const shareToEmail = () => {
  let body = '';
  body += displayName.value;
  body += `\n ${props.subject ? props.subject : ''}`;
  body += `\n ${currentUrl.value}`;

  shareByEmail(displayName.value, body);
};

const sharingOptions = [
  {
    key: 'components.google_classroom',
    title: 'components.label_google_classroom',
    bgColor: '#558b2f',
    icon: 'mdi-google-classroom',
    link: 'https://classroom.google.com/share?url=',
  },
  {
    key: 'microsoft_teams',
    title: 'components.label_microsoft_teams',
    bgColor: '#4951B9',
    icon: 'mdi-microsoft-teams',
    link: 'https://teams.microsoft.com/share?href=',
  },
  {
    key: 'email',
    title: 'components.label_email',
    bgColor: '#AEE3DA',
    icon: 'mdi-email-outline',
    callback: () => {
      shareToEmail();
    },
  },
];

const handleClose = () => {
  emit('close');
};
</script>

<template>
  <v-dialog
    v-model="showDialog"
    transition="scale-transition"
    scrollable
    :max-width="600"
    :max-height="700"
    class="rounded-lg"
    aria-labelledby="share_dialog_title"
    @keydown.esc="handleClose"
    @click:outside="handleClose"
  >
    <v-card>
      <v-card-title class="py-6 ma-0 pb-0">
        <h2 id="share_dialog_title" class="text-center ma-0">{{ title }}</h2>
        <DialogCloseButton @click="handleClose" />
      </v-card-title>
      <v-card-text>
        <SharingOptions
          :show-title="false"
          :sharing-items="sharingOptions"
          :isAvailableToShare="true"
          :entity-url="currentUrl"
          :with-default-options="false"
          :entity-title="displayName"
          :share-entity-url="currentUrl"
          content="video"
        >
        </SharingOptions>
      </v-card-text>
      <div class="share-video"></div>
    </v-card>
  </v-dialog>
</template>

<style scoped lang="scss">
$dark-blue: #054e94;

:deep(.v-card-item) {
  padding-top: 4px;
  padding-bottom: 4px;
  padding-right: 4px;
}

.input-url {
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 6px 12px;
  &::selection {
    background: $dark-blue;
  }
}

.share-buttons {
  padding: 7px 0 4px;
  display: flex;
  align-items: center;
  justify-content: space-around;
}

.share_to_classroom {
  display: flex;
  align-items: center;
  font-size: 10px;
  img {
    margin-right: 5px;
    max-width: 26px;
  }
}
</style>
